import './index.css'

import { SegmentedControl } from '@radix-ui/themes'
import { MdRemoveRedEye as MdRemoveRedEyeIcon } from '@react-icons/all-files/md/MdRemoveRedEye'
import get from 'lodash/get'
import * as React from 'react'

import mixpanel from '@disco/api/mixpanel'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { saveTrackMetadata } from '@disco/state-management/actions/PlaylistTrackActions'
import {
  floatOnly,
  integerOnly,
  isAlbumUnknown,
  isArtistUnknown,
  plural,
} from '@disco/utils/string-utils'
import { isAggregatedSearchTrack } from '@disco/utils/tracks'

import type { EditableTrack, Track, TrackId } from 'types/track'

import { getRightsDataLookupRoute } from 'utils/routing'

import ClipboardInput from 'components/common/clipboard-input'
import EditableImage from 'components/common/editable-image'
import Row from 'components/common/row'
import HelpTooltip from 'components/common/tooltip'
import CopyMetadataButton from 'components/common/tracks/copy-metadata-button'
import { RightsDataLookupCallout } from 'components/common/tracks/rights-data-lookup-callout'
import { Badge, Box, Tooltip } from 'components/ds'
import DialogFooter from 'components/modals/dialog-footer'
import { buildRightsDataQuery } from 'components/modals/track/rights-modal/utils'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import Checkbox from 'components/ui/checkbox'
import DatePicker from 'components/ui/date-picker'
import Flex from 'components/ui/flex'
import TextField from 'components/ui/text-field'

import ExpandableTextField from './expandable-text-field'
import FormField from './form-field'
import ReadOnlyValue from './read-only-value'
import SplitsInfoPopover from './splits-info-popover'
import TrackAliasTable from './track-alias-table'

const MIXED_LABEL = 'Mixed'
type Props = {
  editedTrack: EditableTrack & {
    loadingMetadata?: boolean
    loadingMetadataError?: boolean
    trackIds?: TrackId[]
  }
  originalEditedTrackMetadata?: Record<string, any>
  showDifference?: boolean
  hideMoreButton?: boolean
  hasUnsavedChanges?: boolean
  track?: Track
  saving?: boolean
  showArtworkSettings?: boolean
  disableArtworkEditing?: boolean
  showExtraMetadata?: boolean
  showSubmitButton?: boolean
  disableTrackTitle?: boolean
  disableTrackOrder?: boolean
  rightsDataEnabled?: boolean
  saveTrackMetadata?: typeof saveTrackMetadata
  sendMixpanelEvent?: typeof sendMixpanelEvent
  updateEditedTrack: (
    track: Record<string, any>,
    imageFile: File | null | undefined,
  ) => any
  beforeLabelRender?: (fieldName: string) => any
  fieldsCustomRenderers?: Record<string, React.ReactNode>
  fieldsHelpText: Record<string, React.ReactNode>
  role?: 'alias' | 'merge' | 'batch-edit' | 'track-edit'
  overrideAlbumArtwork?: boolean
  onOverrideAlbumArtwork?: () => void
  constanceSettings?: Record<string, any>
  showApplyToAllVersions?: boolean
  applyToAllVersions?: boolean
  onApplyToAllVersionsToggle?: () => void
  nestedVersionCount?: number
  nestedStemCount?: number
  /** Whether this business writes Rights-tab publishing splits into track
   * metadata on export — gates the info popover on the Composer field. */
  attachWritersToMetadata?: boolean
  /** Same, for recording splits writing into the Copyright field. */
  attachRecordingRightsToMetadata?: boolean
}
type State = {
  image: string | null | undefined
  imageFile: File | null | undefined
  isExpanded: boolean
  expandDropdown: boolean
}
type FormFieldOptions = {
  showIcon?: boolean
  tooltipId?: string
  tooltipContents?: string
}
const showIconOptions = {
  showIcon: true,
}
const hideIconOptions = {
  showIcon: false,
}
const rowColumns = {
  c6_6: [6, 6],
  c12: [12],
  c6_3_3: [6, 3, 3],
  c6_2_2_2: [6, 2, 2, 2],
  c3_3_3_3: [3, 3, 3, 3],
  c2_3_2_3_2: [2, 3, 2, 3, 2],
}
/**
 * MetadataFields component for editing track metadata
 *
 * A comprehensive React class component that provides a complete interface for editing
 * track metadata including title, artist, album, artwork, and extended metadata fields.
 * Supports batch editing, difference highlighting, validation, custom field renderers,
 * and multiple editing roles (alias, merge, batch-edit, track-edit).
 *
 * @param props - Component props
 * @param props.editedTrack - Track being edited with metadata and loading states
 * @param props.originalEditedTrackMetadata - Original metadata for difference comparison
 * @param props.showDifference - Whether to highlight metadata differences
 * @param props.hideMoreButton - Whether to hide the expand/collapse button
 * @param props.hasUnsavedChanges - Whether there are unsaved changes
 * @param props.track - Original track data for reference
 * @param props.saving - Whether metadata is currently being saved
 * @param props.showArtworkSettings - Whether to show artwork configuration options
 * @param props.disableArtworkEditing - Whether artwork editing is disabled
 * @param props.showExtraMetadata - Whether to show extended metadata fields
 * @param props.showSubmitButton - Whether to show the save/submit button
 * @param props.disableTrackTitle - Whether track title editing is disabled
 * @param props.disableTrackOrder - Whether track order editing is disabled
 * @param props.updateEditedTrack - Callback to update the edited track data
 * @param props.beforeLabelRender - Optional function to render content before field labels
 * @param props.fieldsCustomRenderers - Custom renderers for specific fields
 * @param props.fieldsHelpText - Help text for metadata fields
 * @param props.role - Editing context role for layout and behavior changes
 * @param props.overrideAlbumArtwork - Whether to override album artwork for all tracks
 * @param props.onOverrideAlbumArtwork - Callback for album artwork override toggle
 * @param props.constanceSettings - Settings from Constance for feature flags or configurations
 * @returns JSX element containing the complete metadata editing interface
 */
export default class MetadataFields extends React.Component<Props, State> {
  state: State = {
    isExpanded: true,
    // TODO: clean-up "hideMoreButton" business setting
    image: null,
    imageFile: null,
    expandDropdown: false,
  }
  static defaultProps: {
    fieldsHelpText: {
      Artwork: React.ReactNode
    }
  } = {
    fieldsHelpText: {
      Artwork: (
        <div className="EditTrackMetadata_HelpText">
          For best results, upload a square, 1400px .jpg or .png image
        </div>
      ),
    },
  }
  _loadImage: (imageFile: File) => void = (imageFile: File) => {
    const reader = new FileReader()

    reader.onload = e => {
      // check if we still need to set image, as situation might change while
      // image is being read
      if (this.props.editedTrack.metadata.Artwork?.value === imageFile) {
        const result = e.target?.result
        if (typeof result === 'string') {
          this.setState({ image: result })
        }
      }
    }

    reader.readAsDataURL(imageFile)
  }

  componentDidMount() {
    // Properties for event #6 from https://disco-team.atlassian.net/browse/DISCO-5985
    mixpanel.sendPureEvent('Open modal', {
      Modal: 'Metadata',
      'Track ID': this.props.track?.id,
      Catalog: this.props.track?.origin?.source_library?.id,
      'Catalog Business': this.props.track?.origin?.business?.id,
      ...(isAggregatedSearchTrack(this.props.track)
        ? {
            Context: 'Aggregated search',
          }
        : {}),
    })
    const artworkValue = this.props.editedTrack.metadata.Artwork?.value
    if (artworkValue instanceof File) this._loadImage(artworkValue)
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const nextArtworkValue =
      nextProps.editedTrack.metadata.Artwork?.value ?? null
    const currentArtworkFile =
      this.props.editedTrack.metadata.Artwork?.value instanceof File
        ? this.props.editedTrack.metadata.Artwork.value
        : null

    if (
      nextProps.editedTrack.id !== this.props.editedTrack.id ||
      !nextArtworkValue
    ) {
      this.setState({
        image: null,
      })
    } else if (
      nextArtworkValue instanceof File &&
      nextArtworkValue.name !== currentArtworkFile?.name &&
      nextArtworkValue.size !== currentArtworkFile?.size
    ) {
      this._loadImage(nextArtworkValue)
    }
  }

  _updateMetadataField(
    key: string,
    value: string | number | null | typeof undefined,
    fileValue?: File | null | undefined,
  ) {
    const editedTrack = this.props.editedTrack
    const metadata = editedTrack.metadata
    this.props.updateEditedTrack(
      {
        ...editedTrack,
        metadata: {
          ...metadata,
          [key]: { ...metadata[key], mixed: false, value },
        },
      },
      fileValue,
    )
  }

  _handleMetadataFieldChange(
    key: string,
    evt: React.ChangeEvent<HTMLInputElement>,
  ) {
    this._updateMetadataField(key, evt.target.value)
  }

  _handleTrackOrderChange(
    key: string,
    evt: React.ChangeEvent<HTMLInputElement>,
  ) {
    let value = integerOnly(evt.target.value)

    this._updateMetadataField(key, value)
  }

  _handleBpmChange(key: string, evt: React.ChangeEvent<HTMLInputElement>) {
    let value = floatOnly(evt.target.value)

    this._updateMetadataField(key, value)
  }

  _handleArtworkChange: (
    image: any,
    imageFile: File | null | undefined,
    triggerUpdate?: boolean,
  ) => void = (
    image: any,
    imageFile: File | null | undefined,
    triggerUpdate: boolean = true,
  ) => {
    this.setState({
      image,
      imageFile,
    })
    if (!triggerUpdate) return
    const { originalEditedTrackMetadata } = this.props
    const originalArtwork = originalEditedTrackMetadata
      ? originalEditedTrackMetadata.Artwork.value
      : null

    if (imageFile === null) {
      if (originalArtwork) {
        // remove artwork
        this._updateMetadataField('Artwork', null)
      } else {
        // reset changes for correct value of `hasUnsavedChanges`
        this._updateMetadataField('Artwork', originalArtwork)
      }
    } else {
      this._updateMetadataField('Artwork', 'IMAGE_FILE', imageFile)
    }
  }

  _handleExpandToggle() {
    const { isExpanded } = this.state
    this.setState({
      isExpanded: !isExpanded,
    })
  }

  _hasEnoughInfoForRightsDataQuery: () => boolean = () =>
    !!this.props.track &&
    (!!this.props.track.metadata.ISRC ||
      (!!this.props.track.artist && !!this.props.track.name))

  _validateTracks(): string {
    if (this.props.disableTrackOrder) {
      return ''
    }

    const { TrackNumber, Tracks } = this.props.editedTrack.metadata
    const hasTrackNumber = TrackNumber ? !!TrackNumber.value : false
    const hasTracks = Tracks ? !!Tracks.value : false

    if (!hasTrackNumber && hasTracks) {
      return 'Track number is required'
    } else {
      return ''
    }
  }

  _handleSubmit: () => void = () => {
    if (this.props.saveTrackMetadata) {
      this.props.saveTrackMetadata({
        imageFile: this.state.imageFile,
      })
    }
    if (this.props.sendMixpanelEvent) {
      this.props.sendMixpanelEvent('Metadata edit')
    }
  }

  render(): React.ReactElement<'div'> {
    const { showSubmitButton, disableArtworkEditing } = this.props
    const showFullMetadata = this.props.showExtraMetadata
    const artwork = this.props.editedTrack.metadata.Artwork
    const artworkValue = artwork?.value
    const image = !disableArtworkEditing
      ? this.state.image || artworkValue
      : (this.props.track && this.props.track.artwork) ||
        get(this.props.track, 'previews[0].image', null)
    const { Artist, Album } = this.props.editedTrack.metadata
    const isArtistNotSet = !Artist?.mixed && isArtistUnknown(Artist?.value)
    const isAlbumNotSet = !Album?.mixed && isAlbumUnknown(Album?.value)
    const hasEnoughForRightsQuery = this._hasEnoughInfoForRightsDataQuery()
    const rightsDataQuery = buildRightsDataQuery(this.props.track)
    const artworkCheckboxEnabled =
      (this.props.applyToAllVersions && !!artworkValue) ||
      (((artworkValue &&
        typeof artworkValue === 'object' &&
        artworkValue.type) ||
        artworkValue === null) &&
        !isArtistNotSet &&
        !isAlbumNotSet)
    const albumTrackOverrideCheckbox = this.props.showArtworkSettings ? (
      <Checkbox
        key="artworkLabel"
        className="EditMetadata_ArtworkLabel"
        disabled={!artworkCheckboxEnabled}
        checked={this.props.overrideAlbumArtwork}
        onChange={this.props.onOverrideAlbumArtwork}
        label="Update art for all tracks"
      />
    ) : null
    const blankStateMessage = artwork?.mixed
      ? 'Mixed'
      : 'Drag and drop image here, or click to browse'
    const showAliases =
      this.props.editedTrack.id &&
      this.props.track &&
      this.props.track.aliases &&
      this.props.track.aliases.length ? (
        <HelpTooltip
          position="top"
          maxWidth="36rem"
          customScrollbars
          base={
            <div className="EditTrackMetadata_ShowAlias">
              <MdRemoveRedEyeIcon size="1.5em" />
              <div id="EditTrackMetadata_ShowAlias_TooltipAttach" /> Show
              aliases
            </div>
          }
        >
          <TrackAliasTable
            currentMetadata={this.props.editedTrack.metadata}
            track={this.props.track}
          />
        </HelpTooltip>
      ) : null

    const imageField = this._renderFormField(
      'Artwork',
      showFullMetadata ? 'Track Artwork' : 'File preview',
      showIconOptions,
      [
        <EditableImage
          image={image ?? undefined}
          onImageSet={this._handleArtworkChange}
          disableEditing={disableArtworkEditing || this.props.track?.read_only}
          blankStateMessage={blankStateMessage} // TODO: clean-up fieldsHelpText
          // helpText={this.props.fieldsHelpText.Artwork}
          key="component"
        />,
        albumTrackOverrideCheckbox,
      ],
    )

    const { nestedVersionCount, nestedStemCount, showApplyToAllVersions } =
      this.props
    const nestedBadgeText = (() => {
      const versionPart =
        (nestedVersionCount ?? 0) > 0
          ? plural(nestedVersionCount ?? 0, 'Version', 'Versions')
          : null
      const stemPart =
        (nestedStemCount ?? 0) > 0
          ? plural(nestedStemCount ?? 0, 'Stem', 'Stems')
          : null
      return [versionPart, stemPart].filter(Boolean).join(' + ') || null
    })()

    return (
      <div>
        {showApplyToAllVersions && (
          <Box mb="3">
            <SegmentedControl.Root
              // SLAM-1943: Set these explicitly. They previously came from a global defaultProps mutation that React 19 ignores on function components.
              size="2"
              variant="classic"
              radius="large"
              value={
                this.props.applyToAllVersions ? 'all-versions' : 'this-track'
              }
              onValueChange={() => this.props.onApplyToAllVersionsToggle?.()}
              className="EditTrackMetadata_VersionScope"
            >
              <SegmentedControl.Item value="this-track">
                Main track
              </SegmentedControl.Item>
              <SegmentedControl.Item value="all-versions">
                All versions
                {nestedBadgeText && (
                  <Badge color="sky" variant="soft" size="1" ml="2">
                    {nestedBadgeText}
                  </Badge>
                )}
              </SegmentedControl.Item>
            </SegmentedControl.Root>
          </Box>
        )}
        <div className="EditTrackMetadata">
          {this._isNarrowLayout() ? null : (
            <div className="EditTrackMetadata_SplitArt">{imageField}</div>
          )}
          <div className="EditTrackMetadata_SplitMeta">
            {showFullMetadata
              ? this._renderExtraFields(imageField)
              : this._renderTitleField()}
            {showAliases}
            {this.props.rightsDataEnabled &&
              hasEnoughForRightsQuery &&
              this.props.track && (
                <RightsDataLookupCallout
                  stacked
                  to={
                    getRightsDataLookupRoute(
                      window.location.pathname,
                      this.props.track.id,
                    ) + rightsDataQuery
                  }
                />
              )}
          </div>
        </div>

        <DialogFooter
          primary={
            showSubmitButton &&
            !this.props.track?.read_only && (
              <ButtonContainer>
                {!this.props.editedTrack.id &&
                  this.props.editedTrack.trackIds && (
                    <CopyMetadataButton
                      trackIds={this.props.editedTrack.trackIds}
                    />
                  )}
                {this.props.editedTrack.id && (
                  <CopyMetadataButton trackIds={[this.props.editedTrack.id]} />
                )}

                <Button
                  appearance="primary"
                  onClick={this._handleSubmit}
                  caption={
                    !this.props.showApplyToAllVersions
                      ? 'Save'
                      : this.props.applyToAllVersions
                      ? 'Save to all versions'
                      : 'Save to main track'
                  }
                  disabled={
                    this.props.saving ||
                    !this.props.hasUnsavedChanges ||
                    !!this._validateTracks()
                  }
                  progressCaption="Saving metadata"
                  progressIndicator={this.props.saving}
                />
              </ButtonContainer>
            )
          }
          secondary={(() => {
            const showClipboard =
              this.props.showExtraMetadata &&
              this.state.isExpanded &&
              this.props.track &&
              this.props.role !== 'alias'

            if (!showClipboard) return null
            return (
              <div className="EditMetadata_FooterSecondaryLayout">
                <div className="EditMetadata_FooterArtSlot">
                  {showClipboard && (
                    <div className="EditTrackMetadata_SecondaryFooter">
                      <div className="ClipboardInput">
                        <ClipboardInput
                          compactClipboardInput
                          label="ID: "
                          value={this.props.track.id.toString()}
                          tooltipLabel="Copy ID"
                          withoutIcon
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })()}
        />
      </div>
    )
  }

  _isNarrowLayout(): boolean {
    const { role } = this.props
    return role === 'alias' || role === 'merge'
  }

  _renderTitleField(): React.ReactNode {
    return (
      <Row columnsSize={rowColumns.c6_6}>
        {this._renderFormField('Title', 'Title', hideIconOptions)}
      </Row>
    )
  }

  _renderExtraFields(
    imageField: React.ReactNode,
  ): null | React.ReactElement<'div'> {
    const { isExpanded } = this.state
    const { role } = this.props

    const editExternalTrackId =
      this.props.constanceSettings?.EDIT_EXTERNAL_TRACK_ID

    const recordingRightsEnabled =
      this.props.constanceSettings?.RIGHTS_DATA_SHOW_RECORDING_TAB

    return isExpanded ? (
      <div className="EditTrackMetadata_ExtraFields">
        {this._isNarrowLayout() ? (
          <div className="grid-cols-8">
            <div className="grid-col-span-4 grid-row-span-5">{imageField}</div>
            <div className="grid-col-span-4">
              {this._renderFormField('Title', 'Title', hideIconOptions)}
            </div>
            <div className="grid-col-span-4">
              {this._renderFormField('Artist', 'Artist', showIconOptions)}
            </div>
            <div className="grid-col-span-4">
              {this._renderFormField('Album', 'Album', showIconOptions)}
            </div>
            <div className="grid-col-span-4">
              {this._renderFormField('Composer', 'Composer', showIconOptions)}
            </div>
          </div>
        ) : (
          <div className="grid-cols-8">
            <div className="grid-col-span-4">
              {this._renderFormField('Title', 'Title', hideIconOptions)}
            </div>
            <div className="grid-col-span-4">
              {this._renderFormField('Artist', 'Artist', showIconOptions)}
            </div>
            <div className="grid-col-span-4">
              {this._renderFormField('Album', 'Album', showIconOptions)}
            </div>
            <div className="grid-col-span-4">
              {this._renderFormField('Composer', 'Composer', showIconOptions)}
            </div>
          </div>
        )}
        <div className="grid-cols-8">
          <div className="grid-col-span-4">
            {this._renderFormField('Grouping', 'Grouping', showIconOptions)}
          </div>
          <div className="grid-col-span-4">
            {this._renderFormField('Genre', 'Genre', showIconOptions)}
          </div>
          {recordingRightsEnabled && (
            <>
              <div className="grid-col-span-4">
                {this._renderFormField(
                  'Copyright',
                  'Copyright',
                  showIconOptions,
                )}
              </div>
              {/* Spacer keeps the following fields on their own row */}
              {editExternalTrackId ? (
                <div className="grid-col-span-4">
                  {this._renderFormField(
                    'External Id',
                    'External Id',
                    showIconOptions,
                  )}
                </div>
              ) : (
                <div className="grid-col-span-4" />
              )}
            </>
          )}
          <div className="grid-col-span-1">
            {this._renderFormField(
              'Year',
              'Year',
              showIconOptions,
              this._renderMetadataYearField('Year'),
            )}
          </div>
          <div className="grid-col-span-2">
            {this._renderFormField(
              'Release Date',
              'Release Date',
              showIconOptions,
              this._renderMetadataDateField('Release Date'),
            )}
          </div>
          <div className="grid-col-span-1">
            {this._renderFormField(
              'BPM',
              'BPM',
              showIconOptions,
              this._renderMetadataBpmField('BPM'),
            )}
          </div>
          <div className="grid-col-span-2">
            {this._renderFormField('ISRC', 'ISRC', showIconOptions)}
          </div>
          <div className="grid-col-span-2">
            {this._renderFormField(
              'TrackNumber',
              'Order',
              showIconOptions,
              <div>
                <Flex spacing="small">
                  {this._renderMetadataTrackOrderField('TrackNumber')}{' '}
                  <span>/</span>
                  {this._renderMetadataTrackOrderField('Tracks')}
                  <div className="EditMetadata_TrackNumberError">
                    {this._validateTracks()}
                  </div>
                </Flex>
              </div>,
            )}
          </div>
          {/* Shown next to Copyright above when the recording flag is on */}
          {!!editExternalTrackId && !recordingRightsEnabled && (
            <div className="grid-col-span-4">
              {this._renderFormField(
                'External Id',
                'External Id',
                showIconOptions,
              )}
            </div>
          )}
          <div className="grid-col-span-full">
            {this._renderFormField(
              'Comment',
              'Comments',
              showIconOptions,
              this._renderMetadataTextareaField('Comment'),
            )}
          </div>
          {role === 'alias' && (
            <div className="grid-col-span-full">
              {this._renderFormField(
                'X-Disco-StripUnknown',
                'Turn off any other metadata fields not displayed here',
                {
                  ...hideIconOptions,
                  tooltipId: 'tooltip_metadata_modal_strip_unknown_meta',
                },
                <span />,
              )}
            </div>
          )}
          {/* TODO: pro_number hidden for now - delete or return */}
          {/* {this._isNarrowLayout() ? null : ( */}
          {/*   <Row columnsSize={rowColumns.c3_3_3_3}> */}
          {/*     {this._renderFormField('ProNumber', 'PRO Number', { */}
          {/*       ...showIconOptions, */}
          {/*       tooltipId: 'tooltip_metadata_modal_pro_number', */}
          {/*     })} */}
          {/*   </Row> */}
          {/* )} */}
        </div>
      </div>
    ) : null
  }

  _renderFormField(
    fieldName: string,
    label: string,
    options: FormFieldOptions,
    component?: React.ReactNode,
  ): React.ReactElement<React.ComponentProps<any>, any> {
    const {
      beforeLabelRender,
      fieldsHelpText,
      fieldsCustomRenderers,
      showDifference,
    } = this.props

    // The Writers tab becomes "Publishing" once the recording rights tab is
    // enabled, so the Composer popover follows the same flag to stay in sync.
    const composerSplitsTerm = this.props.constanceSettings
      ?.RIGHTS_DATA_SHOW_RECORDING_TAB
      ? 'Publishing'
      : 'Writer'

    const labelIcon = this._rightsFieldEnabled(fieldName) ? (
      <SplitsInfoPopover
        title={fieldName}
        description={
          fieldName === 'Composer'
            ? `${composerSplitsTerm} splits will be appended to Composer in the following format:`
            : 'Recording splits will be appended to Copyright in the following format:'
        }
        example={
          fieldName === 'Composer'
            ? 'Writer %, Publisher, PRO'
            : 'Copyright holder, Owner %'
        }
      />
    ) : null

    return (
      <FormField
        className="FormField"
        beforeLabel={beforeLabelRender && beforeLabelRender(fieldName)}
        labelIcon={labelIcon}
        helpText={
          fieldName !== 'Artwork' && fieldsHelpText && fieldsHelpText[fieldName]
        }
        component={
          (fieldsCustomRenderers && fieldsCustomRenderers[fieldName]) ||
          component ||
          this._renderMetadataField(fieldName)
        }
        label={label}
        options={options}
        showDifference={showDifference}
        fieldName={fieldName}
        metadata={this.props.editedTrack.metadata}
        originalMetadata={this.props.originalEditedTrackMetadata}
      />
    )
  }

  _renderExpandButton(): null | React.ReactElement<'button'> {
    const { isExpanded } = this.state
    if (this.props.hideMoreButton) return null
    return (
      <button
        className="EditTrackMetadata_ExpandBtn"
        onClick={this._handleExpandToggle.bind(this)}
        type="button"
      >
        {isExpanded ? '− Less' : '+ More'}
      </button>
    )
  }

  _renderMetadataTextareaField(key: string): null | React.ReactNode {
    const field = this.props.editedTrack.metadata[key]
    const readOnly = !!this.props.track?.read_only
    if (!field) return null

    if (readOnly) {
      return <ReadOnlyValue value={field.value ?? ''} mixed={field.mixed} />
    }

    return (
      <TextField
        key={key}
        value={field.value ?? undefined}
        multiline={3}
        onChange={this._handleMetadataFieldChange.bind(this, key)}
        placeholder={field.mixed ? MIXED_LABEL : ''}
        compact={false}
      />
    )
  }

  _renderMetadataYearField(key: string): null | React.ReactNode {
    const field = this.props.editedTrack.metadata[key]
    const readOnly = !!this.props.track?.read_only
    if (!field) return null

    if (readOnly) {
      return <ReadOnlyValue value={field.value ?? ''} mixed={field.mixed} />
    }

    return (
      <TextField
        onChange={this._handleMetadataFieldChange.bind(this, key)}
        placeholder={field.mixed ? MIXED_LABEL : ''}
        value={field.value ?? undefined}
        maskInput="1111"
        compact={false}
      />
    )
  }

  _renderMetadataDateField(key: string): React.ReactNode {
    const field = this.props.editedTrack.metadata[key]
    const readOnly = !!this.props.track?.read_only

    if (readOnly) {
      return <ReadOnlyValue value={field?.value ?? ''} mixed={field?.mixed} />
    }

    return (
      <DatePicker
        key={this.props.editedTrack.id}
        value={field?.value}
        placeholder={field?.mixed ? MIXED_LABEL : undefined}
        onChange={value => this._updateMetadataField(key, value)}
        compact={false}
      />
    )
  }

  _renderMetadataBpmField(key: string): null | React.ReactNode {
    const field = this.props.editedTrack.metadata[key]
    const readOnly = !!this.props.track?.read_only
    if (!field) return null

    if (readOnly) {
      return <ReadOnlyValue value={field.value ?? ''} mixed={field.mixed} />
    }

    return (
      <TextField
        onChange={this._handleBpmChange.bind(this, key)}
        placeholder={field.mixed ? MIXED_LABEL : ''}
        value={field.value ?? undefined}
        compact={false}
      />
    )
  }

  _renderMetadataField(key: string): React.ReactNode {
    const field = this.props.editedTrack.metadata[key]
    const titleDisabledByVersions =
      key === 'Title' && !!this.props.applyToAllVersions
    const isDisabled =
      titleDisabledByVersions ||
      (this.props.disableTrackTitle ? key === 'Title' : false)
    const readOnly = !!this.props.track?.read_only

    if (readOnly) {
      return <ReadOnlyValue value={field?.value || ''} mixed={field?.mixed} />
    }

    const isRightsField = key === 'Composer' || key === 'Copyright'
    const input = isRightsField ? (
      <ExpandableTextField
        value={field?.value || ''}
        onChange={this._handleMetadataFieldChange.bind(this, key)}
        placeholder={field?.mixed ? MIXED_LABEL : ''}
      />
    ) : (
      <TextField
        value={field?.value || ''}
        disabled={isDisabled}
        onChange={this._handleMetadataFieldChange.bind(this, key)}
        placeholder={field?.mixed ? MIXED_LABEL : ''}
        compact={false}
      />
    )
    if (titleDisabledByVersions) {
      return (
        <Tooltip content="Title cannot be updated when applying metadata to all nested versions">
          <div>{input}</div>
        </Tooltip>
      )
    }
    return input
  }

  _rightsFieldEnabled(key: string): boolean {
    const { track, attachWritersToMetadata, attachRecordingRightsToMetadata } =
      this.props
    if (track?.read_only) return false
    return key === 'Composer'
      ? !!attachWritersToMetadata
      : key === 'Copyright' && !!attachRecordingRightsToMetadata
  }

  _renderMetadataTrackOrderField(key: string): null | React.ReactNode {
    const field = this.props.editedTrack.metadata[key]
    const readOnly = !!this.props.track?.read_only
    if (!field) return null

    if (readOnly) {
      return (
        <ReadOnlyValue
          value={field.value ?? ''}
          mixed={field.mixed}
          align="center"
        />
      )
    }

    return (
      <TextField
        onChange={this._handleTrackOrderChange.bind(this, key)}
        maxLength={3}
        disabled={this.props.disableTrackOrder}
        placeholder={field.mixed ? MIXED_LABEL : ''}
        value={field.value ?? undefined}
        compact={false}
        textAlign="center"
      />
    )
  }
}
